import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Loader2, FileSpreadsheet, FileText, Copy, Check, ClipboardList, Percent } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Calendar Helpers ────────────────────────────────────────
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1; // Mon=0 ... Sun=6
}
function formatDate(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function isWeekend(y, m, d) {
  const dow = new Date(y, m, d).getDay();
  return dow === 0 || dow === 6;
}
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const inrFmt = n => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const PaymentBadge = ({ s }) => {
  const id = `payment-badge-${s.student_id}`;
  if (s.payment_status === 'overdue') return (
    <span className="inline-flex flex-wrap gap-1" data-testid={id}>
      <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200 whitespace-nowrap">Overdue{s.overdue_amount > 0 ? ` ${inrFmt(s.overdue_amount)}` : ''}</Badge>
      {s.on_hold && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">On hold</Badge>}
    </span>
  );
  if (s.payment_status === 'paid') return <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={id}>Paid</Badge>;
  if (s.payment_status === 'partly_paid') return <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 whitespace-nowrap" data-testid={id} title={`Balance ${inrFmt(s.due_amount)} (not overdue yet)`}>Partly paid</Badge>;
  return <span className="text-[10px] text-slate-300" data-testid={id}>—</span>;
};

// ─── DPR Report Component ────────────────────────────────────
function DprReport({ date, apiPrefix, trainerId, trainerName }) {
  const [dpr, setDpr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    const url = trainerId
      ? `${apiPrefix}/dpr/${date}?trainer_id=${trainerId}`
      : `${apiPrefix}/dpr/${date}`;
    api.get(url)
      .then(r => setDpr(r.data))
      .catch(() => toast.error('Failed to load DPR'))
      .finally(() => setLoading(false));
  }, [date, apiPrefix, trainerId]);

  if (loading) return <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>;
  if (!dpr || !dpr.rows?.length) return <p className="text-xs text-slate-400 text-center py-8">No attendance data for this date</p>;

  const cols = [
    { key: 'batch_number', label: 'Batch No.' },
    { key: 'batch_name', label: 'Batch Name' },
    { key: 'batch_conducted', label: 'Conducted' },
    { key: 'timing', label: 'Timing' },
    { key: 'batch_strength', label: 'Strength' },
    { key: 'present', label: 'Present' },
    { key: 'absent', label: 'Absent' },
    { key: 'mode_of_training', label: 'Mode' },
    { key: 'not_taken_reason', label: 'Reason' },
    { key: 'trainer_comments', label: 'Comments' },
  ];

  const copyToClipboard = () => {
    const header = `DPR - ${dpr.day_name}, ${date}\nTrainer: ${dpr.trainer_name}\n\n`;
    const lines = dpr.rows.map((r, i) =>
      `${i + 1}. ${r.batch_name} (#${r.batch_number})\n   Conducted: ${r.batch_conducted} | Mode: ${r.mode_of_training}\n   Timing: ${r.timing || '-'}\n   Strength: ${r.batch_strength} (Present: ${r.present}, Absent: ${r.absent})${r.not_taken_reason ? `\n   Reason: ${r.not_taken_reason}` : ''}${r.trainer_comments ? `\n   Comments: ${r.trainer_comments}` : ''}`
    ).join('\n\n');
    navigator.clipboard.writeText(header + lines);
    setCopied(true);
    toast.success('DPR copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const exportExcel = () => {
    const data = dpr.rows.map(r => {
      const obj = {};
      cols.forEach(c => { obj[c.label] = r[c.key]; });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'DPR');
    saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]), `DPR_${date}.xlsx`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16); doc.text(`Daily Progress Report`, 14, 18);
    doc.setFontSize(10); doc.text(`${dpr.day_name}, ${date} | Trainer: ${dpr.trainer_name}`, 14, 26);
    autoTable(doc, {
      startY: 32,
      head: [cols.map(c => c.label)],
      body: dpr.rows.map(r => cols.map(c => r[c.key] ?? '-')),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [109, 40, 217] },
    });
    doc.save(`DPR_${date}.pdf`);
  };

  return (
    <div className="space-y-3" data-testid="dpr-report">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-800">Daily Progress Report</h3>
          <p className="text-xs text-slate-400">{dpr.day_name}, {date} &middot; {dpr.trainer_name}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={copyToClipboard} data-testid="dpr-copy-btn">
            {copied ? <Check className="w-3 h-3 mr-1 text-emerald-500" /> : <Copy className="w-3 h-3 mr-1" />}{copied ? 'Copied' : 'Copy'}
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={exportExcel} data-testid="dpr-excel-btn"><FileSpreadsheet className="w-3 h-3 mr-1" />Excel</Button>
          <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={exportPdf} data-testid="dpr-pdf-btn"><FileText className="w-3 h-3 mr-1" />PDF</Button>
        </div>
      </div>
      <Card className="border p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            {cols.map(c => <TableHead key={c.key} className="text-[9px] uppercase">{c.label}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {dpr.rows.map((r, i) => (
              <TableRow key={i} className={r.batch_conducted === 'No' ? 'bg-slate-50/50' : ''}>
                <TableCell className="text-xs font-bold text-violet-600">#{r.batch_number}</TableCell>
                <TableCell className="text-xs font-medium">{r.batch_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[9px] ${r.batch_conducted === 'Yes' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{r.batch_conducted}</Badge>
                </TableCell>
                <TableCell className="text-[10px] font-mono text-slate-500">{r.timing || '-'}</TableCell>
                <TableCell className="text-xs">{r.batch_strength}</TableCell>
                <TableCell className="text-xs font-semibold text-emerald-600">{r.present}</TableCell>
                <TableCell className={`text-xs font-semibold ${r.absent > 0 ? 'text-rose-600' : 'text-slate-300'}`}>{r.absent}</TableCell>
                <TableCell><Badge variant="outline" className="text-[9px]">{r.mode_of_training}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ─── Date Modal (Batch attendance marking) ───────────────────
function DateModal({ date, open, onClose, apiPrefix, trainerId, readOnly, savePrefix }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayInfo, setDayInfo] = useState({});
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [localState, setLocalState] = useState({});
  const [saving, setSaving] = useState(null);
  const [showDpr, setShowDpr] = useState(false);

  useEffect(() => {
    if (!open || !date) return;
    setLoading(true);
    setShowDpr(false);
    setExpandedBatch(null);
    const url = trainerId
      ? `${apiPrefix}/${date}?trainer_id=${trainerId}`
      : `${apiPrefix}/${date}`;
    api.get(url).then(r => {
      setBatches(r.data.batches || []);
      setDayInfo(r.data);
      const state = {};
      (r.data.batches || []).forEach(b => {
        state[b.batch_id] = {
          class_taken: b.has_record ? b.class_taken : null,
          mode_of_training: b.mode_of_training || '',
          not_taken_reason: b.not_taken_reason || '',
          trainer_comments: b.trainer_comments || '',
          completion_percentage: b.completion_percentage != null ? b.completion_percentage : null,
          students: b.students || [],
        };
      });
      setLocalState(state);
    }).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, [open, date, apiPrefix, trainerId]);

  const loadStudents = async (batchId) => {
    if (expandedBatch === batchId) { setExpandedBatch(null); return; }
    setExpandedBatch(batchId);
    // Always fetch fresh student data from API (for phone/email)
    setLoadingStudents(true);
    try {
      const studentUrl = `${savePrefix || apiPrefix}/${date}/${batchId}/students`;
      const r = await api.get(studentUrl);
      // Merge saved attendance status with fresh student data
      const savedMap = {};
      (localState[batchId]?.students || []).forEach(s => { savedMap[s.student_id] = s.status; });
      const s = (r.data.students || []).map(st => ({
        ...st,
        status: savedMap[st.student_id] || st.status || 'present',
      }));
      setStudents(s);
      setLocalState(prev => ({ ...prev, [batchId]: { ...prev[batchId], students: s } }));
    } catch { setStudents([]); }
    setLoadingStudents(false);
  };

  const toggleClassTaken = (batchId, val) => {
    setLocalState(prev => ({
      ...prev,
      [batchId]: { ...prev[batchId], class_taken: val },
    }));
  };

  const setMode = (batchId, mode) => {
    setLocalState(prev => ({
      ...prev,
      [batchId]: { ...prev[batchId], mode_of_training: mode },
    }));
  };

  const setNotTakenReason = (batchId, reason) => {
    setLocalState(prev => ({
      ...prev,
      [batchId]: { ...prev[batchId], not_taken_reason: reason },
    }));
  };

  const setTrainerComments = (batchId, comments) => {
    setLocalState(prev => ({
      ...prev,
      [batchId]: { ...prev[batchId], trainer_comments: comments },
    }));
  };

  const setCompletionPercentage = (batchId, pct) => {
    setLocalState(prev => ({
      ...prev,
      [batchId]: { ...prev[batchId], completion_percentage: pct },
    }));
  };

  // ─── Auto-fetch completion % from course tracker ───
  const [batchCompletionPcts, setBatchCompletionPcts] = useState({});

  const fetchBatchCompletion = useCallback(async (batchId) => {
    try {
      const res = await api.get(`/courses/completions/${batchId}`);
      if (res.data.linked && res.data.completion_pct != null) {
        setBatchCompletionPcts(prev => ({ ...prev, [batchId]: res.data.completion_pct }));
        setLocalState(prev => ({
          ...prev,
          [batchId]: { ...prev[batchId], completion_percentage: res.data.completion_pct },
        }));
      }
    } catch {}
  }, []);

  // Fetch completion for all batches on load
  useEffect(() => {
    batches.forEach(b => fetchBatchCompletion(b.batch_id));
  }, [batches, fetchBatchCompletion]);

  const toggleStudent = (batchId, studentId) => {
    setLocalState(prev => {
      const batch = prev[batchId] || {};
      const updated = (batch.students || []).map(s =>
        s.student_id === studentId ? { ...s, status: s.status === 'present' ? 'absent' : 'present' } : s
      );
      return { ...prev, [batchId]: { ...batch, students: updated } };
    });
    setStudents(prev => prev.map(s =>
      s.student_id === studentId ? { ...s, status: s.status === 'present' ? 'absent' : 'present' } : s
    ));
  };

  const markAllPresent = (batchId) => {
    setLocalState(prev => {
      const batch = prev[batchId] || {};
      const updated = (batch.students || []).map(s => ({ ...s, status: 'present' }));
      return { ...prev, [batchId]: { ...batch, students: updated } };
    });
    setStudents(prev => prev.map(s => ({ ...s, status: 'present' })));
  };

  const saveBatch = async (batchId) => {
    const state = localState[batchId];
    if (!state) return;
    if (state.class_taken === null) {
      toast.error('Please mark class as Taken or Not Taken');
      return;
    }
    if (state.class_taken && !state.mode_of_training) {
      toast.error('Please select Mode of Training');
      return;
    }
    if (state.class_taken === false && !state.not_taken_reason?.trim()) {
      toast.error('Please enter the reason for class not taken');
      return;
    }
    // completion_percentage auto-calculated from course tracker
    const compPct = batchCompletionPcts[batchId] ?? state.completion_percentage ?? 0;
    setSaving(batchId);
    try {
      const saveUrl = `${savePrefix || apiPrefix}/${date}/${batchId}`;
      await api.post(saveUrl, {
        class_taken: state.class_taken,
        mode_of_training: state.mode_of_training,
        not_taken_reason: state.not_taken_reason || '',
        trainer_comments: state.trainer_comments || '',
        completion_percentage: compPct,
        students: state.class_taken ? state.students : [],
      });
      toast.success('Attendance saved');
    } catch { toast.error('Failed to save'); }
    setSaving(null);
  };

  const saveAll = async () => {
    const toSave = batches.filter(b => localState[b.batch_id]?.class_taken !== null);
    if (toSave.length === 0) {
      toast.error('No batches with attendance decision');
      return;
    }
    for (const b of toSave) {
      const state = localState[b.batch_id];
      if (state?.class_taken && !state?.mode_of_training) {
        toast.error(`Select mode for ${b.name || b.batch_number}`);
        return;
      }
      if (state?.class_taken === false && !state?.not_taken_reason?.trim()) {
        toast.error(`Enter reason for not taken: ${b.name || b.batch_number}`);
        return;
      }
    }
    for (const b of toSave) {
      await saveBatch(b.batch_id);
    }
  };

  const markedCount = batches.filter(b => localState[b.batch_id]?.class_taken !== null).length;
  const displayStudents = expandedBatch ? (localState[expandedBatch]?.students || students) : [];

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="attendance-modal">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-xl font-bold tracking-tight" data-testid="modal-date-title">
              {dayInfo.day_name}, {date}
            </DialogTitle>
            <Badge variant="outline" className={`text-[10px] font-semibold uppercase ${dayInfo.is_weekend ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>
              {dayInfo.is_weekend ? 'Weekend' : 'Weekday'}
            </Badge>
            <div className="flex-1" />
            <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setShowDpr(!showDpr)} data-testid="toggle-dpr-btn">
              <ClipboardList className="w-3 h-3 mr-1" />{showDpr ? 'Back to Attendance' : 'View DPR'}
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            {showDpr ? 'Daily Progress Report' : readOnly ? 'View attendance records' : 'Mark attendance for your batches'}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading ? (
            <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>
          ) : showDpr ? (
            <DprReport date={date} apiPrefix={apiPrefix} trainerId={trainerId} trainerName={dayInfo.trainer_name} />
          ) : batches.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-12">No batches for this date</p>
          ) : (
            batches.map(b => {
              const state = localState[b.batch_id] || {};
              const isExpanded = expandedBatch === b.batch_id;
              const batchStudents = isExpanded ? displayStudents : [];
              const presentCount = batchStudents.filter(s => s.status === 'present').length;
              const classTaken = state.class_taken;

              return (
                <div key={b.batch_id} className={`border rounded-lg overflow-hidden transition-all ${classTaken === true ? 'border-emerald-200 ring-1 ring-emerald-100' : classTaken === false ? 'border-rose-200 ring-1 ring-rose-100' : 'border-slate-200'}`} data-testid={`batch-card-${b.batch_id}`}>
                  {/* Batch Header - click to expand/collapse */}
                  <div
                    className={`px-5 py-3.5 flex items-center gap-4 cursor-pointer select-none ${classTaken === true ? 'bg-emerald-50/30' : classTaken === false ? 'bg-rose-50/20' : 'bg-white hover:bg-slate-50/50'}`}
                    onClick={() => loadStudents(b.batch_id)}
                    data-testid={`batch-header-${b.batch_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-slate-800">{b.name || b.course_name}</p>
                        <span className="text-xs font-mono text-slate-400">#{b.batch_number}</span>
                        {classTaken === true && <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">Class Taken</Badge>}
                        {classTaken === false && <Badge className="text-[9px] bg-rose-100 text-rose-700 border-rose-200">Not Taken</Badge>}
                        {(batchCompletionPcts[b.batch_id] ?? state.completion_percentage) != null && <Badge className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">{batchCompletionPcts[b.batch_id] ?? state.completion_percentage}%</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="text-xs text-slate-400">{b.centre_name}</span>
                        <span className="text-[10px] text-slate-300">|</span>
                        <span className="text-xs text-slate-400">{b.student_count} students</span>
                        {b.timing && <><span className="text-[10px] text-slate-300">|</span><span className="text-xs text-slate-500 font-mono">{b.timing}</span></>}
                        {b.start_date && <><span className="text-[10px] text-slate-300">|</span><span className="text-[10px] text-slate-400">{b.start_date} to {b.end_date || '—'}</span></>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {state.mode_of_training && <Badge variant="outline" className="text-[9px]">{state.mode_of_training}</Badge>}
                      <Badge variant="outline" className={`text-[9px] ${b.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700' : 'bg-violet-50 text-violet-700'}`}>{b.batch_status}</Badge>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t bg-white">
                      {/* Class Taken / Not Taken toggle + Mode */}
                      {!readOnly && (
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
                          <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider mr-1">Class Status:</span>
                          <div className="inline-flex rounded-md overflow-hidden border border-slate-200" data-testid={`class-toggle-${b.batch_id}`}>
                            <button
                              className={`px-3.5 py-1.5 text-[11px] font-semibold transition-colors ${classTaken === true ? 'bg-emerald-500 text-white' : 'bg-white text-slate-400 hover:bg-emerald-50'}`}
                              onClick={() => toggleClassTaken(b.batch_id, true)}
                              data-testid={`class-taken-btn-${b.batch_id}`}
                            >Class Taken</button>
                            <button
                              className={`px-3.5 py-1.5 text-[11px] font-semibold transition-colors ${classTaken === false ? 'bg-rose-500 text-white' : 'bg-white text-slate-400 hover:bg-rose-50'}`}
                              onClick={() => toggleClassTaken(b.batch_id, false)}
                              data-testid={`class-not-taken-btn-${b.batch_id}`}
                            >Not Taken</button>
                          </div>
                          <Select value={state.mode_of_training || '_none'} onValueChange={v => setMode(b.batch_id, v === '_none' ? '' : v)} disabled={readOnly}>
                            <SelectTrigger className={`w-32 h-8 text-xs ${classTaken === true && !state.mode_of_training ? 'border-red-300 ring-1 ring-red-100' : ''}`} data-testid={`mode-select-${b.batch_id}`}>
                              <SelectValue placeholder="Select Mode *" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none" disabled>Select Mode *</SelectItem>
                              <SelectItem value="Online">Online</SelectItem>
                              <SelectItem value="Offline">Offline</SelectItem>
                            </SelectContent>
                          </Select>
                          <Badge variant="outline" className="text-xs h-8 px-3 flex items-center gap-1.5 bg-violet-50 text-violet-700 border-violet-200" data-testid={`completion-auto-${b.batch_id}`}>
                            <Percent className="w-3 h-3" />
                            {batchCompletionPcts[b.batch_id] ?? state.completion_percentage ?? 0}% Course Covered
                          </Badge>
                          <div className="flex-1" />
                          <Button size="sm" className="h-8 text-xs bg-violet-600 hover:bg-violet-500 px-5" onClick={() => saveBatch(b.batch_id)} disabled={saving === b.batch_id} data-testid={`save-batch-${b.batch_id}`}>
                            {saving === b.batch_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
                          </Button>
                        </div>
                      )}
                      {readOnly && (
                        <div className="px-5 py-2 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
                          <span className="text-[10px] uppercase font-semibold text-slate-400">Class:</span>
                          {classTaken === true ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Taken</Badge> : classTaken === false ? <Badge className="text-[9px] bg-rose-100 text-rose-700">Not Taken</Badge> : <Badge variant="outline" className="text-[9px]">Not Marked</Badge>}
                          {state.mode_of_training && <Badge variant="outline" className="text-[9px]">{state.mode_of_training}</Badge>}
                          {state.completion_percentage != null && <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">{batchCompletionPcts[b.batch_id] ?? state.completion_percentage}% Course Covered</Badge>}
                        </div>
                      )}

                      {/* Not Taken Reason */}
                      {classTaken === false && (
                        <div className="px-5 py-3 border-b border-slate-100 bg-rose-50/30">
                          <label className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider block mb-1.5">Reason for not taking class *</label>
                          {readOnly ? (
                            <p className="text-xs text-slate-700">{state.not_taken_reason || '—'}</p>
                          ) : (
                            <textarea
                              value={state.not_taken_reason || ''}
                              onChange={e => setNotTakenReason(b.batch_id, e.target.value)}
                              placeholder="Enter reason (e.g., Holiday, Trainer leave, No students available...)"
                              className={`w-full text-xs border rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-rose-300 ${!state.not_taken_reason?.trim() ? 'border-red-300' : 'border-slate-200'}`}
                              rows={2}
                              data-testid={`not-taken-reason-${b.batch_id}`}
                            />
                          )}
                        </div>
                      )}

                      {/* Course Topics Tracker — always shown when a syllabus is linked, interactive only when class is taken */}
                      <CourseTopicsPrototype
                        batchName={b.batch_name}
                        completionPct={state.completion_percentage}
                        batchId={b.batch_id}
                        readOnly={readOnly || classTaken !== true}
                        readOnlyReason={classTaken === false ? 'Class not taken today' : classTaken === null ? 'Mark "Class Taken" to tick topics' : null}
                        onCompletionChange={(pct) => {
                          setBatchCompletionPcts(prev => ({ ...prev, [b.batch_id]: pct }));
                          setLocalState(prev => ({ ...prev, [b.batch_id]: { ...prev[b.batch_id], completion_percentage: pct } }));
                        }}
                      />

                      {/* Student List */}
                      {classTaken !== false && (
                        <div>
                          <div className="px-5 py-2.5 flex items-center justify-between border-b border-slate-100">
                            <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Student Attendance</p>
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] text-slate-400"><span className="font-semibold text-emerald-600">{presentCount}</span> / {batchStudents.length} present</span>
                              {!readOnly && (
                                <Button variant="outline" size="sm" className="h-6 text-[10px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200" onClick={() => markAllPresent(b.batch_id)} data-testid={`mark-all-present-${b.batch_id}`}>
                                  Mark All Present
                                </Button>
                              )}
                            </div>
                          </div>
                          {loadingStudents ? (
                            <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-violet-500 mx-auto" /></div>
                          ) : (
                            <Table>
                              <TableHeader><TableRow className="bg-slate-50/70">
                                <TableHead className="text-[9px] uppercase w-8">#</TableHead>
                                <TableHead className="text-[9px] uppercase">Name</TableHead>
                                <TableHead className="text-[9px] uppercase">Payment</TableHead>
                                <TableHead className="text-[9px] uppercase">Enrollment</TableHead>
                                <TableHead className="text-[9px] uppercase">Phone</TableHead>
                                <TableHead className="text-[9px] uppercase">Email</TableHead>
                                <TableHead className="text-[9px] uppercase text-center w-28">Status</TableHead>
                              </TableRow></TableHeader>
                              <TableBody>
                                {batchStudents.map((s, i) => (
                                  <TableRow key={s.student_id} className={s.status === 'absent' ? 'bg-rose-50/30' : ''}>
                                    <TableCell className="text-xs text-slate-400">{i + 1}</TableCell>
                                    <TableCell className="text-xs font-medium text-slate-800">{s.name}</TableCell>
                                    <TableCell><PaymentBadge s={s} /></TableCell>
                                    <TableCell className="text-xs font-mono text-slate-500">{s.enrollment_no}</TableCell>
                                    <TableCell className="text-xs text-slate-500">{s.phone}</TableCell>
                                    <TableCell className="text-xs text-slate-500 truncate max-w-[140px]">{s.email}</TableCell>
                                    <TableCell className="text-center">
                                      {readOnly ? (
                                        <Badge variant="outline" className={`text-[9px] ${s.status === 'present' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                          {s.status === 'present' ? 'Present' : 'Absent'}
                                        </Badge>
                                      ) : (
                                        <div className="inline-flex rounded-md overflow-hidden border border-slate-200" data-testid={`student-toggle-${s.student_id}`}>
                                          <button
                                            className={`px-2.5 py-1 text-[10px] font-semibold transition-colors ${s.status === 'present' ? 'bg-emerald-500 text-white' : 'bg-white text-slate-400 hover:bg-slate-50'}`}
                                            onClick={() => s.status !== 'present' && toggleStudent(b.batch_id, s.student_id)}
                                          >Present</button>
                                          <button
                                            className={`px-2.5 py-1 text-[10px] font-semibold transition-colors ${s.status === 'absent' ? 'bg-rose-500 text-white' : 'bg-white text-slate-400 hover:bg-slate-50'}`}
                                            onClick={() => s.status !== 'absent' && toggleStudent(b.batch_id, s.student_id)}
                                          >Absent</button>
                                        </div>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      )}
                      {classTaken === false && (
                        <div className="px-5 py-6 text-center">
                          <p className="text-xs text-slate-400">Class was not taken — no student attendance to mark</p>
                        </div>
                      )}

                      {/* Trainer Comments (optional, always visible when class status is set) */}
                      {classTaken !== null && (
                        <div className="px-5 py-3 border-t border-slate-100">
                          <label className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider block mb-1.5">Comments (optional)</label>
                          {readOnly ? (
                            state.trainer_comments ? <p className="text-xs text-slate-700">{state.trainer_comments}</p> : <p className="text-xs text-slate-300 italic">No comments</p>
                          ) : (
                            <textarea
                              value={state.trainer_comments || ''}
                              onChange={e => setTrainerComments(b.batch_id, e.target.value)}
                              placeholder="Any observations, topics covered, issues..."
                              className="w-full text-xs border border-slate-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-violet-300"
                              rows={2}
                              data-testid={`trainer-comments-${b.batch_id}`}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {!showDpr && !loading && batches.length > 0 && !readOnly && (
          <div className="px-6 py-3 border-t flex items-center justify-between flex-shrink-0 bg-slate-50/50">
            <p className="text-xs text-slate-400">
              <span className="font-semibold text-emerald-600">{markedCount}</span> of <span className="font-semibold">{batches.length}</span> batches marked
            </p>
            <Button className="bg-violet-600 hover:bg-violet-500 text-xs font-semibold px-6" onClick={saveAll} data-testid="save-all-attendance-btn">
              Save All Attendance
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Calendar Grid ───────────────────────────────────────────
function AttendanceCalendar({ calendarData, year, month, onDateClick, selectedDate }) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const todayStr = formatDate(today.getFullYear(), today.getMonth(), today.getDate());

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="bg-white border rounded-lg p-5" data-testid="attendance-calendar">
      <div className="grid grid-cols-7 gap-0">
        {DAY_HEADERS.map((d, i) => (
          <div key={d} className={`text-center text-[10px] uppercase font-semibold tracking-wider pb-3 ${i >= 5 ? 'text-violet-400' : 'text-slate-400'}`}>{d}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={`e-${i}`} className="h-[72px] border border-slate-100 bg-slate-50/50" />;
          const dateStr = formatDate(year, month, d);
          const wknd = isWeekend(year, month, d);
          const isToday = dateStr === todayStr;
          const sel = dateStr === selectedDate;
          const info = calendarData[dateStr];
          const dotColor = info?.status === 'complete' ? 'bg-emerald-500' : info?.status === 'partial' ? 'bg-amber-500' : null;

          return (
            <div
              key={dateStr}
              data-testid={`calendar-day-${dateStr}`}
              onClick={() => onDateClick(dateStr)}
              className={`h-[72px] border border-slate-100 p-2 cursor-pointer transition-colors relative group
                ${wknd ? 'bg-violet-50/30 hover:bg-violet-50' : 'hover:bg-violet-50/50'}
                ${sel ? 'ring-2 ring-violet-500 ring-inset z-10' : ''}
                ${isToday ? 'bg-violet-50/60' : ''}
              `}
            >
              <span className={`text-sm font-medium ${wknd ? 'text-violet-600' : 'text-slate-700'} ${isToday ? 'bg-violet-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs' : ''} group-hover:text-violet-700`}>{d}</span>
              {dotColor && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
                  <span className={`w-1.5 h-1.5 rounded-full ${dotColor} block`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Exported Component ─────────────────────────────────
export default function TrainerDailyAttendancePage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [calendarData, setCalendarData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  const fetchCalendar = useCallback(() => {
    api.get(`/trainer/daily-attendance/calendar?month=${monthStr}`)
      .then(r => setCalendarData(r.data.dates || {}))
      .catch(() => {});
  }, [monthStr]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    fetchCalendar(); // refresh dots
  };

  // Summary stats
  const totalMarked = Object.values(calendarData).filter(d => d.status === 'complete').length;
  const partialMarked = Object.values(calendarData).filter(d => d.status === 'partial').length;

  return (
    <div className="space-y-5" data-testid="trainer-daily-attendance">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Daily Attendance</h1>
        <p className="text-sm text-slate-400 mt-0.5">Click on a date to mark attendance for your batches</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border p-4">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Fully Marked</p>
          <p className="text-2xl font-bold text-emerald-600">{totalMarked}<span className="text-sm font-normal text-slate-400 ml-1">days</span></p>
        </Card>
        <Card className="border p-4">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Partially Marked</p>
          <p className="text-2xl font-bold text-amber-600">{partialMarked}<span className="text-sm font-normal text-slate-400 ml-1">days</span></p>
        </Card>
        <Card className="border p-4">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Legend</p>
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2 h-2 rounded-full bg-emerald-500" />All marked</span>
            <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2 h-2 rounded-full bg-amber-500" />Partial</span>
          </div>
        </Card>
      </div>

      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">{MONTH_NAMES[month]} {year}</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="w-8 h-8" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={goToday} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" className="w-8 h-8" onClick={nextMonth} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <AttendanceCalendar
        calendarData={calendarData}
        year={year}
        month={month}
        onDateClick={handleDateClick}
        selectedDate={selectedDate}
      />

      {/* Modal */}
      <DateModal
        date={selectedDate}
        open={modalOpen}
        onClose={handleModalClose}
        apiPrefix="/trainer/daily-attendance"
        readOnly={false}
      />
    </div>
  );
}

// ─── Admin Daily Attendance Page ─────────────────────────────
export function AdminDailyAttendancePage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [trainers, setTrainers] = useState([]);
  const [trainerId, setTrainerId] = useState('');
  const [calendarData, setCalendarData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  useEffect(() => {
    api.get('/training/daily-attendance/trainers').then(r => setTrainers(r.data.trainers || [])).catch(() => {});
  }, []);

  const fetchCalendar = useCallback(() => {
    if (!trainerId) { setCalendarData({}); return; }
    api.get(`/training/daily-attendance/calendar?month=${monthStr}&trainer_id=${trainerId}`)
      .then(r => setCalendarData(r.data.dates || {}))
      .catch(() => {});
  }, [monthStr, trainerId]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const handleDateClick = (dateStr) => {
    if (!trainerId) { toast.error('Select a trainer first'); return; }
    setSelectedDate(dateStr);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    fetchCalendar();
  };

  const totalMarked = Object.values(calendarData).filter(d => d.status === 'complete').length;
  const partialMarked = Object.values(calendarData).filter(d => d.status === 'partial').length;

  return (
    <div className="space-y-5" data-testid="admin-daily-attendance">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Daily Attendance</h1>
          <p className="text-sm text-slate-400 mt-0.5">View and review trainer attendance records</p>
        </div>
        <Select value={trainerId || 'none'} onValueChange={v => setTrainerId(v === 'none' ? '' : v)}>
          <SelectTrigger className="w-64 h-9 text-xs" data-testid="admin-trainer-select"><SelectValue placeholder="Select Trainer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none" disabled>Select Trainer</SelectItem>
            {trainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!trainerId ? (
        <Card className="border p-16 text-center">
          <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Select a trainer to view their attendance calendar</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card className="border p-4">
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Fully Marked</p>
              <p className="text-2xl font-bold text-emerald-600">{totalMarked}<span className="text-sm font-normal text-slate-400 ml-1">days</span></p>
            </Card>
            <Card className="border p-4">
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Partially Marked</p>
              <p className="text-2xl font-bold text-amber-600">{partialMarked}<span className="text-sm font-normal text-slate-400 ml-1">days</span></p>
            </Card>
            <Card className="border p-4">
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Legend</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2 h-2 rounded-full bg-emerald-500" />All marked</span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2 h-2 rounded-full bg-amber-500" />Partial</span>
              </div>
            </Card>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800">{MONTH_NAMES[month]} {year}</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="w-8 h-8" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={goToday}>Today</Button>
              <Button variant="outline" size="icon" className="w-8 h-8" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>

          <AttendanceCalendar calendarData={calendarData} year={year} month={month} onDateClick={handleDateClick} selectedDate={selectedDate} />

          <DateModal
            date={selectedDate}
            open={modalOpen}
            onClose={handleModalClose}
            apiPrefix="/training/daily-attendance"
            savePrefix="/training/daily-attendance"
            trainerId={trainerId}
            readOnly={false}
          />
        </>
      )}
    </div>
  );
}


// ─── Course Topics Tracker (Live API) ──────────────
function CourseTopicsPrototype({ batchName, completionPct, batchId, onCompletionChange, readOnly = false, readOnlyReason = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (!batchId) { setLoading(false); return; }
    api.get(`/courses/completions/${batchId}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [batchId]);

  const toggleTopic = async (subtopicId) => {
    if (!batchId || readOnly) return;
    try {
      await api.post(`/courses/completions/${batchId}/toggle`, { subtopic_id: subtopicId });
      const fresh = await api.get(`/courses/completions/${batchId}`);
      setData(fresh.data);
      if (onCompletionChange) onCompletionChange(fresh.data.completion_pct);
    } catch {
      toast.error('Failed to update topic');
    }
  };

  if (loading) return <div className="mx-5 my-3 rounded-lg border-2 border-dashed border-violet-200 px-4 py-3 text-xs text-violet-400 animate-pulse">Loading course syllabus...</div>;
  if (!data || !data.linked || !data.syllabus) return null;

  const { syllabus, completions, completion_pct } = data;
  const totalTopics = (syllabus.modules || []).reduce((s, m) => s + (m.subtopics?.length || 0), 0);
  const completedTopics = (syllabus.modules || []).reduce((s, m) => s + (m.subtopics || []).filter(st => completions[st.id]?.completed).length, 0);

  return (
    <div className={`mx-5 my-3 rounded-lg border-2 bg-white overflow-hidden shadow-sm ${readOnly ? 'border-slate-200 opacity-90' : 'border-violet-200'}`} data-testid="course-topics-tracker">
      {/* Read-only hint banner */}
      {readOnly && readOnlyReason && (
        <div className="px-4 py-1.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2" data-testid="course-tracker-readonly-banner">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
          <p className="text-[10px] text-amber-800 font-medium">{readOnlyReason} — topics visible but locked</p>
        </div>
      )}
      {/* Prominent Header */}
      <button onClick={() => setIsOpen(!isOpen)} className={`w-full flex items-center gap-3 px-4 py-3 transition-all ${readOnly ? 'bg-gradient-to-r from-slate-500 to-slate-400 hover:from-slate-600 hover:to-slate-500' : 'bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-700 hover:to-violet-600'}`}>
        <div className="w-8 h-8 rounded-md bg-white/20 flex items-center justify-center flex-shrink-0">
          <ClipboardList className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-xs font-bold text-white tracking-wide">Course Topics Tracker</p>
          <p className="text-[10px] text-violet-200">{syllabus.name} &middot; {completedTopics}/{totalTopics} topics</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-lg font-black text-white leading-none">{completion_pct}%</p>
            <p className="text-[9px] text-violet-200">covered</p>
          </div>
          <div className="w-20 h-2.5 bg-white/20 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${completion_pct >= 80 ? 'bg-emerald-400' : completion_pct >= 40 ? 'bg-amber-400' : 'bg-white'}`} style={{ width: `${completion_pct}%` }} />
          </div>
          {isOpen ? <ChevronUp className="w-4 h-4 text-white/70" /> : <ChevronDown className="w-4 h-4 text-white/70" />}
        </div>
      </button>

      {isOpen && (
        <div className="p-3 space-y-1.5 bg-slate-50/50">
          {(syllabus.modules || []).map((mod, mi) => {
            const modCompleted = (mod.subtopics || []).filter(st => completions[st.id]?.completed).reduce((s, st) => s + st.weight, 0);
            const modTotal = mod.weight;
            const modPct = modTotal > 0 ? Math.round((modCompleted / modTotal) * 100) : 0;
            const isExpanded = expanded[mi];
            const allDone = (mod.subtopics || []).every(st => completions[st.id]?.completed);
            const doneCount = (mod.subtopics || []).filter(st => completions[st.id]?.completed).length;

            return (
              <div key={mod.id} className={`bg-white border rounded-md overflow-hidden ${allDone ? 'border-emerald-200' : 'border-slate-150'}`}>
                <button onClick={() => setExpanded(p => ({ ...p, [mi]: !p[mi] }))} className={`w-full flex items-center gap-3 px-3.5 py-2.5 transition-colors ${allDone ? 'bg-emerald-50/50 hover:bg-emerald-50' : 'hover:bg-slate-50'}`}>
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${allDone ? 'bg-emerald-500 text-white' : modPct > 0 ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>
                    {allDone ? <Check className="w-3.5 h-3.5" /> : mi + 1}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <span className={`text-xs font-semibold ${allDone ? 'text-emerald-700' : 'text-slate-800'}`}>{mod.name}</span>
                    <span className="text-[9px] text-slate-400 ml-2">{doneCount}/{(mod.subtopics || []).length} topics</span>
                  </div>
                  <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${allDone ? 'bg-emerald-500' : 'bg-violet-400'}`} style={{ width: `${modPct}%` }} />
                  </div>
                  <span className={`text-[10px] font-bold w-9 text-right ${allDone ? 'text-emerald-600' : modPct > 0 ? 'text-violet-600' : 'text-slate-400'}`}>{modPct}%</span>
                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-300" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-300" />}
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {(mod.subtopics || []).map((st) => {
                      const comp = completions[st.id] || {};
                      return (
                        <div key={st.id} className={`flex items-center gap-3 px-3.5 py-2 border-b border-slate-50 last:border-0 ${comp.completed ? 'bg-emerald-50/40' : (readOnly ? '' : 'hover:bg-slate-50/50')}`}>
                          <button
                            onClick={() => toggleTopic(st.id)}
                            disabled={readOnly}
                            title={readOnly ? (readOnlyReason || 'Locked') : ''}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${comp.completed ? 'bg-emerald-500 border-emerald-500 shadow-sm shadow-emerald-200' : readOnly ? 'border-slate-200 bg-slate-50 cursor-not-allowed' : 'border-slate-300 hover:border-violet-400 hover:shadow-sm'}`}
                            data-testid={`topic-check-${st.id}`}
                          >
                            {comp.completed && <Check className="w-3 h-3 text-white" />}
                          </button>
                          <span className={`text-xs flex-1 ${comp.completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{st.name}</span>
                          <span className="text-[9px] text-slate-300 font-mono">{st.weight}%</span>
                          {comp.completed && comp.completed_at && (
                            <span className="text-[9px] text-emerald-700 font-medium whitespace-nowrap bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded flex items-center gap-1" data-testid={`topic-stamp-${st.id}`}>
                              {comp.completed_by && (
                                <>
                                  <span className="font-semibold">{comp.completed_by}</span>
                                  <span className="text-emerald-300">&middot;</span>
                                </>
                              )}
                              <span>
                                {new Date(comp.completed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}{' '}
                                {new Date(comp.completed_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </span>
                          )}
                          {!comp.completed && <span className="text-[9px] text-slate-300">pending</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
